import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { PortalConsent } from "../../features/portal/models/PortalProfile";
import {
  fetchPortalConsents,
  withdrawPortalConsent,
} from "../../features/portal/slice-extensions/portal-me-slice-extension";

interface Props {
  memberId: string;
}

interface RowProps {
  consent: PortalConsent;
  busy: boolean;
  onWithdraw: (consentId: string) => Promise<void>;
}

const ConsentRow = ({ consent, busy, onWithdraw }: RowProps) => {
  const { t, i18n } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-grey-20 dark:text-white">
            {t(`members.consents.purposes.${consent.purpose}`)}
          </p>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {t("portal.me.consentVersion", { version: consent.documentVersion, date: formatDate(consent.grantedAt) })}
            {consent.grantedByGuardian && ` · ${t("portal.me.byGuardian")}`}
          </p>
          {consent.withdrawnAt && (
            <p className="text-sm text-grey-40 dark:text-grey-80">
              {t("portal.me.withdrawn", { date: formatDate(consent.withdrawnAt) })}
            </p>
          )}
        </div>
        {consent.withdrawable && !confirming && (
          <button type="button" className="btn btn-secondary shrink-0" onClick={() => setConfirming(true)}>
            {t("portal.me.withdraw")}
          </button>
        )}
      </div>
      {consent.withdrawable && confirming && (
        <div className="mt-2">
          <p className="text-sm text-grey-20 dark:text-white">{t("portal.me.withdrawConfirm")}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setConfirming(false)}>
              {t("common.cancel", "Cancel")}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onWithdraw(consent.id)}>
              {t("portal.me.withdraw")}
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * Consents (PD-0036): every record with its version and dates, who gave it, and Withdraw on the active
 * marketing consent only — terms and privacy end with erasure, the card below.
 */
const PortalConsentsCard = ({ memberId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [consents, setConsents] = useState<PortalConsent[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setConsents(null);
    dispatch(fetchPortalConsents(memberId))
      .unwrap()
      .then((loaded) => active && setConsents(loaded))
      .catch(() => active && setConsents(null));
    return () => {
      active = false;
    };
  }, [dispatch, memberId]);

  if (consents === null) return null;

  const withdraw = async (consentId: string) => {
    setBusy(true);
    setError(false);
    try {
      const withdrawn = await dispatch(withdrawPortalConsent({ memberId, consentId })).unwrap();
      setConsents((list) => (list ?? []).map((item) => (item.id === withdrawn.id ? withdrawn : item)));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="text-base font-semibold text-grey-20 dark:text-white">{t("portal.me.consents")}</h2>
      {consents.length === 0 ? (
        <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">{t("portal.me.noConsents")}</p>
      ) : (
        <ul className="mt-1 divide-y divide-grey-90 dark:divide-primary-20">
          {consents.map((consent) => (
            <ConsentRow key={consent.id} consent={consent} busy={busy} onWithdraw={withdraw} />
          ))}
        </ul>
      )}
      {error && <p className="field-error mt-2">{t("portal.me.withdrawFailed")}</p>}
    </div>
  );
};

export default PortalConsentsCard;
